import type { Feature, FeatureCollection, Point, Polygon } from 'geojson';

export const TRACES_GEO_SOURCE_POINTS = 'traces-geo-points-src';
export const TRACES_GEO_SOURCE_POLYGONS = 'traces-geo-polygons-src';
export const TRACES_GEO_LAYER_POINTS = 'traces-geo-points';
export const TRACES_GEO_LAYER_LABELS = `${TRACES_GEO_LAYER_POINTS}-label`;
export const TRACES_GEO_LAYER_POLYGONS = 'traces-geo-polygons';
export const TRACES_GEO_COLOR = '#00838f';

export interface TracesGeometryLayers {
  points: FeatureCollection<Point>;
  polygons: FeatureCollection<Polygon>;
}

export interface TracesLayerMap {
  getSource(id: string): { setData(data: FeatureCollection): void } | undefined;
  addSource(id: string, source: object): unknown;
  getLayer(id: string): unknown;
  addLayer(layer: object): unknown;
  setLayoutProperty(layerId: string, property: string, value: string): unknown;
}

const emptyCollection = <G extends Point | Polygon>(): FeatureCollection<G> => ({
  type: 'FeatureCollection',
  features: [],
});

const areaLabel = (hectares: unknown): string =>
  typeof hectares === 'number' && Number.isFinite(hectares) ? `${hectares.toFixed(2)} ha` : '';

export function tracesGeometryLayer(traces: FeatureCollection | undefined): TracesGeometryLayers {
  const layers: TracesGeometryLayers = { points: emptyCollection<Point>(), polygons: emptyCollection<Polygon>() };
  for (const feature of traces?.features ?? []) {
    if (feature.geometry?.type === 'Point') {
      const properties = { ...feature.properties, label: areaLabel(feature.properties?.['Area']) };
      layers.points.features.push({ ...feature, properties, geometry: feature.geometry } as Feature<Point>);
    } else if (feature.geometry?.type === 'Polygon') {
      layers.polygons.features.push({ ...feature, geometry: feature.geometry } as Feature<Polygon>);
    }
  }
  return layers;
}

export function sourceIndexOf(traces: FeatureCollection | undefined, derivedIndex: number): number {
  const sourceFeature = traces?.features?.[derivedIndex]?.properties?.['sourceFeature'];
  return typeof sourceFeature === 'number' ? sourceFeature : derivedIndex;
}

export class TracesGeometryLayerController {
  visible = false;
  private layers: TracesGeometryLayers = tracesGeometryLayer(undefined);

  constructor(private readonly map: TracesLayerMap) {}

  setData(traces: FeatureCollection | undefined): void {
    this.layers = tracesGeometryLayer(traces);
    this.pushData();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (visible) this.ensureLayers();
    this.applyVisibility();
  }

  private ensureLayers(): void {
    this.ensureSource(TRACES_GEO_SOURCE_POINTS);
    this.ensureSource(TRACES_GEO_SOURCE_POLYGONS);
    this.ensureLayer({
      id: TRACES_GEO_LAYER_POLYGONS,
      type: 'line',
      source: TRACES_GEO_SOURCE_POLYGONS,
      paint: { 'line-color': TRACES_GEO_COLOR, 'line-width': 2, 'line-dasharray': [2, 1] },
    });
    this.ensureLayer({
      id: TRACES_GEO_LAYER_POINTS,
      type: 'circle',
      source: TRACES_GEO_SOURCE_POINTS,
      paint: {
        'circle-radius': 6,
        'circle-color': TRACES_GEO_COLOR,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });
    this.ensureLayer({
      id: TRACES_GEO_LAYER_LABELS,
      type: 'symbol',
      source: TRACES_GEO_SOURCE_POINTS,
      layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-offset': [0, 1.2], 'text-anchor': 'top' },
      paint: { 'text-color': TRACES_GEO_COLOR, 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
    });
    this.pushData();
  }

  private ensureSource(id: string): void {
    if (!this.map.getSource(id)) this.map.addSource(id, { type: 'geojson', data: emptyCollection() });
  }

  private ensureLayer(layer: { id: string; [property: string]: unknown }): void {
    if (!this.map.getLayer(layer.id)) this.map.addLayer(layer);
  }

  private pushData(): void {
    this.map.getSource(TRACES_GEO_SOURCE_POINTS)?.setData(this.layers.points);
    this.map.getSource(TRACES_GEO_SOURCE_POLYGONS)?.setData(this.layers.polygons);
  }

  private applyVisibility(): void {
    const value = this.visible ? 'visible' : 'none';
    for (const id of [TRACES_GEO_LAYER_POLYGONS, TRACES_GEO_LAYER_POINTS, TRACES_GEO_LAYER_LABELS]) {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', value);
    }
  }
}
